<template>
  <teleport to="body">
    <div v-if="show" class="modal d-block" tabindex="-1" style="z-index: 1080; background: rgba(0, 0, 0, 0.35)" @click.self="close">
      <div class="modal-dialog modal-dialog-centered">
        <form class="modal-content" @submit.prevent="confirm">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t('handover.title') }}</h5>
            <button type="button" class="btn btn-sm btn-icon btn-light" :aria-label="$t('button.close')" @click="close">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <div class="modal-body">
            <div v-if="submitError" class="alert alert-danger py-3 mb-5">{{ submitError }}</div>

            <!-- remise normale : code donné par le client (ou la personne qu'il envoie) -->
            <template v-if="!manual">
              <p class="text-muted mb-5">{{ pickup ? $t('handover.ask_pickup') : $t('handover.ask_delivery') }}</p>
              <div class="d-flex gap-2">
                <input
                  ref="codeInput"
                  v-model="code"
                  type="text"
                  inputmode="numeric"
                  autocomplete="one-time-code"
                  maxlength="6"
                  required
                  pattern="\d{6}"
                  class="form-control form-control-solid handover-code-input"
                  :class="{ 'is-valid': verified, 'is-invalid': codeError }"
                  placeholder="••••••"
                  @input="onCodeInput"
                  @keydown.enter.prevent="verify"
                />
                <button type="button" class="btn btn-light-primary flex-shrink-0" :disabled="code.length !== 6 || isVerifying || verified" @click="verify">
                  <span v-if="isVerifying" class="spinner-border spinner-border-sm"></span>
                  <template v-else><i class="bi bi-check2 me-1"></i>{{ $t('handover.verify') }}</template>
                </button>
              </div>
              <div v-if="codeError" class="text-danger fs-7 mt-2">{{ codeError }}</div>
              <div v-else-if="verified" class="text-success fs-7 mt-2"><i class="bi bi-check-circle-fill me-1"></i>{{ $t('handover.code_ok') }}</div>
              <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3">
                <button type="button" class="btn btn-link btn-sm p-0" :disabled="isResending" @click="resend">
                  <i class="bi bi-whatsapp me-1"></i>{{ $t('handover.resend') }}
                  <span v-if="isResending" class="spinner-border spinner-border-sm ms-1"></span>
                </button>
                <button v-if="canOverride" type="button" class="btn btn-link btn-sm p-0 text-muted" @click="manual = true">
                  {{ $t('handover.without_code') }}
                </button>
              </div>
            </template>

            <!-- sans code : gérant uniquement, avec un motif enregistré dans la commande -->
            <template v-else>
              <div class="alert alert-warning d-flex gap-3 py-3">
                <i class="bi bi-exclamation-triangle fs-3 text-warning"></i>
                <span>{{ $t('handover.without_code_warning') }}</span>
              </div>
              <label class="form-label required">{{ $t('handover.reason') }}</label>
              <textarea v-model="reason" required maxlength="500" rows="3" class="form-control form-control-solid" :placeholder="$t('handover.reason_placeholder')"></textarea>
              <button type="button" class="btn btn-link btn-sm p-0 mt-2" @click="manual = false">
                <i class="bi bi-arrow-left me-1"></i>{{ $t('handover.back_to_code') }}
              </button>
            </template>

            <!-- paiement à la livraison : le montant à encaisser, rappelé avant de confirmer -->
            <div v-if="amountToCollect" class="handover-collect mt-6">
              <i class="bi bi-cash-coin"></i>
              <span>{{ $t('handover.to_collect') }}</span>
              <strong>{{ amountToCollect }}</strong>
            </div>

            <div class="mt-6">
              <label class="form-label">{{ pickup ? $t('handover.collected_by') : $t('handover.received_by') }}</label>
              <input v-model="collectedBy" type="text" maxlength="255" class="form-control form-control-solid" :placeholder="$t('handover.collected_by_placeholder')" />
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-sm btn-light" @click="close">{{ $t('button.close') }}</button>
            <button type="submit" class="btn btn-sm btn-success fw-bolder" :disabled="isLoading || (!manual && !verified)">
              {{ pickup ? $t('handover.confirm_pickup') : $t('handover.confirm_delivery') }}
              <span v-if="isLoading" class="spinner-border spinner-border-sm align-middle ms-2"></span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </teleport>
</template>

<script>
/**
 * Remise d'une commande en ligne au client : le livreur (ou le vendeur, pour un retrait)
 * saisit le code de remise à 6 chiffres du client. Renvoi du code par WhatsApp ; sans
 * code, un gérant (delivery_handover_override) valide avec un motif.
 * Le code est d'abord vérifié (bouton « Vérifier ») ; la remise n'est confirmable qu'ensuite.
 * `submit` reçoit { handover_code | handover_manual + handover_reason, collected_by } et
 * renvoie une promesse : la fenêtre se ferme si elle aboutit, sinon l'erreur s'affiche
 * dans la fenêtre (requêtes en { silent: true }, sans toastr).
 */
export default {
  name: 'HandoverCodeModal',
  props: {
    show: { type: Boolean, default: false },
    checkoutId: { type: String, default: null },
    pickup: { type: Boolean, default: false },
    canOverride: { type: Boolean, default: false },
    // montant à encaisser, déjà formaté (« 5 495 FCFA ») ; vide si la commande est payée
    amountToCollect: { type: String, default: '' },
    submit: { type: Function, required: true },
  },
  emits: ['update:show'],
  data() {
    return {
      code: '', manual: false, reason: '', collectedBy: '',
      verified: false, codeError: '', submitError: '',
      isLoading: false, isVerifying: false, isResending: false,
    }
  },
  watch: {
    show(open) {
      if (!open) return
      this.code = ''
      this.verified = false
      this.codeError = ''
      this.submitError = ''
      this.manual = false
      this.reason = ''
      this.collectedBy = ''
      this.$nextTick(() => this.$refs.codeInput?.focus())
    },
  },
  methods: {
    close() {
      this.$emit('update:show', false)
    },
    onCodeInput() {
      this.code = this.code.replace(/\D/g, '').slice(0, 6)
      this.verified = false
      this.codeError = ''
    },
    async verify() {
      if (this.code.length !== 6 || this.isVerifying || this.verified) return
      this.isVerifying = true
      this.codeError = ''
      try {
        await window.axios.post(`delivery/${this.checkoutId}/handover-code/verify`, { handover_code: this.code }, { silent: true })
        this.verified = true
      } catch (e) {
        this.codeError = e?.response?.data?.message || this.$t('handover.error')
      } finally {
        this.isVerifying = false
      }
    },
    async confirm() {
      if (this.isLoading || (!this.manual && !this.verified)) return
      this.isLoading = true
      this.submitError = ''
      try {
        await this.submit(this.manual
          ? { handover_manual: true, handover_reason: this.reason, collected_by: this.collectedBy || null }
          : { handover_code: this.code, collected_by: this.collectedBy || null })
        this.close()
      } catch (e) {
        this.submitError = e?.response?.data?.message || this.$t('handover.error')
        if (!this.manual) this.verified = false
      } finally {
        this.isLoading = false
      }
    },
    async resend() {
      this.isResending = true
      try {
        const response = await window.axios.post(`delivery/${this.checkoutId}/handover-code/resend`, null, { silent: true })
        window.toastr.success(response.data.message)
      } catch (e) {
        this.codeError = e?.response?.data?.message || this.$t('handover.error')
      } finally {
        this.isResending = false
      }
    },
  },
}
</script>

<style scoped>
.handover-collect {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 8px;
  background: #fff8dd;
  color: #5e6278;
}
.handover-collect i { font-size: 1.4rem; color: #f6a609; }
.handover-collect strong { margin-left: auto; font-size: 1.15rem; color: #181c32; }
.handover-code-input {
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: 0.6em;
  text-align: center;
  padding-left: 0.6em;
}
</style>
